import React from "react";
import {
  FaChalkboardTeacher,
  FaClipboardCheck,
  FaUserTie,
  FaRegLightbulb
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { motion } from "framer-motion";

const features = [
  {
    step: "01",
    title: "Expert Faculty",
    description:
      "They simplify complex concepts into lucid insights. Through personal guidance, they instill confidence, discipline, and depth.",
    icon: <FaChalkboardTeacher size={32} />,
    watermark: <FaChalkboardTeacher size={120} />,
    bg: "bg-blue-50",
    textAccent: "text-blue-700",
    watermarkColor: "text-blue-200/40",
  },
  {
    step: "02",
    title: "Methodology",
    description:
      "A student-focused pedagogy combining lectures, interactive sessions, and evaluations. Special emphasis on analytical reasoning.",
    icon: <FaRegLightbulb size={32} />,
    watermark: <FaRegLightbulb size={120} />,
    bg: "bg-red-50",
    textAccent: "text-red-700",
    watermarkColor: "text-red-200/40",
  },
  {
    step: "03",
    title: "Test Series",
    description:
      "Real-time mock tests designed as per UPSC standards to build confidence, time management, and accuracy.",
    icon: <FaClipboardCheck size={32} />,
    watermark: <FaClipboardCheck size={120} />,
    bg: "bg-green-50",
    textAccent: "text-green-700",
    watermarkColor: "text-green-200/40",
  },
  {
    step: "04",
    title: "Personal Mentoring",
    description:
      "One-to-one guidance for personalized study plans, progress tracking, and emotional support during prep.",
    icon: <FaUserTie size={32} />,
    watermark: <FaUserTie size={120} />,
    bg: "bg-purple-50",
    textAccent: "text-purple-700",
    watermarkColor: "text-purple-200/40",
  },
];

const Review = () => {
  return (
    <section className="bg-white py-24 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto text-center relative z-10">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-800 text-sm font-bold tracking-wider uppercase shadow-sm">
            Core Values
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">
            Why <span className="text-red-700">Choose Us</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Selva’s IAS Academy is distinguished by its scholarly faculty,
            student-centric approach, and ethical commitment. Our methodology fuses
            rigorous academics with modern innovations for balanced preparation. We
            shape aspirants not only for examination success but also for responsible
            leadership.
          </p>
        </motion.div>

        {/* Carousel on mobile */}
        <div className="sm:hidden text-left">
          <Swiper spaceBetween={20} slidesPerView={1}>
            {features.map((item, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  className={`relative flex flex-col p-8 rounded-[2rem] overflow-hidden transition-all duration-300 h-full ${item.bg}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className={`absolute -bottom-6 -right-6 ${item.watermarkColor} transform -rotate-12 pointer-events-none`}>
                    {item.watermark}
                  </div>
                  <div className="flex justify-between items-start mb-8 relative z-10">
                    <span className={`text-5xl font-black opacity-20 ${item.textAccent}`}>
                      {item.step}
                    </span>
                    <div className={`${item.textAccent}`}>
                      {item.icon}
                    </div>
                  </div>
                  <div className="relative z-10 flex-grow">
                    <h3 className="font-extrabold text-gray-900 text-2xl mb-4">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-base font-medium leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Grid for tablet & desktop */}
        <div className="hidden sm:grid gap-6 sm:grid-cols-2 lg:grid-cols-4 text-left">
          {features.map((item, index) => (
            <motion.div
              key={index}
              className={`group relative flex flex-col p-8 rounded-[2rem] overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-slate-200/50 ${item.bg}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.15 }}
              viewport={{ once: true }}
            >
              {/* Large Watermark Icon */}
              <div className={`absolute -bottom-6 -right-6 ${item.watermarkColor} transform -rotate-12 pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-0`}>
                {item.watermark}
              </div>

              {/* Header: Step & Icon */}
              <div className="flex justify-between items-start mb-8 relative z-10">
                <span className={`text-5xl font-black opacity-20 ${item.textAccent}`}>
                  {item.step}
                </span>
                <div className={`${item.textAccent}`}>
                  {item.icon}
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10 flex-grow">
                <h3 className="font-extrabold text-gray-900 text-2xl mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-base font-medium leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Review;
